/**
 * ASSETT AI CONSOLE — C2b-9: THE RICH TEXT EDITOR AND THE READER.
 *
 * Matt's ruling R7 is the toolbar: headings, bold, italic, underline,
 * strikethrough, bullet / numbered / check lists, highlight and text colour from
 * the fixed palette, links, and inline attachments. Tables, code blocks and
 * @-mentions are out for day one — and per Amendment 1's correction to C2b-7,
 * adding one later is a schema bump and a build, not a ruling.
 *
 * ===========================================================================
 * WHAT THIS FILE IS AND WHAT IT IS NOT
 * ===========================================================================
 *
 * IT EDITS THE DOCUMENT. It does not parse or produce Markdown, and there is no
 * copy of that dialect in the browser: the shadow is the SERVER's, generated on
 * every write from the document this sends. One implementation of what the
 * dialect means, on the server, is the same argument buildListing() makes about
 * the view — a second copy would agree on the day it was written and drift at
 * the first change to either.
 *
 * IT NEVER STORES OR SENDS HTML. The HTML below exists for exactly as long as a
 * person is looking at it: the document is rendered INTO contenteditable blocks,
 * and read back OUT of them by a walker that understands eleven tags and drops
 * everything else to its text. A paste from Word, an injected <script>, a <div>
 * from another site — all of them come back as words. That is the sanitiser, and
 * it runs on the way IN to our model rather than on the way out to a screen.
 *
 * AND THE SERVER VALIDATES AGAIN. This file being careful is not the wall; the
 * wall is that server/src/lib/richtext.ts refuses an unknown node or mark 422 by
 * name, refuses a javascript: or data: URL, and refuses an attachment id
 * belonging to another record. A browser is not a place to keep a rule.
 *
 * ===========================================================================
 * WHY THE BLOCKS ARE FLAT
 * ===========================================================================
 *
 * The document nests: a bullet list holds list items, each holding a paragraph
 * and possibly another list. Editing that shape directly means every keystroke
 * reasoning about a tree. So the editor FLATTENS it into a list of lines, each
 * with a kind and a depth — which is exactly what a person sees, and exactly
 * what the Markdown shadow looks like — and rebuilds the tree on the way out.
 * Tab and Shift+Tab move a line's depth; everything else is a line edit.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// the document, as this file sees it
// ---------------------------------------------------------------------------

export interface RTMark { type: string; attrs?: Record<string, unknown> }
export interface RTNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RTNode[];
  text?: string;
  marks?: RTMark[];
}
export interface RTDoc { type: 'doc'; schemaVersion: number; content: RTNode[] }

/**
 * THE FIXED PALETTE, and it is the same list as the server's on purpose — a
 * colour this offers that the server refuses is a button that 422s, and a colour
 * the server accepts that this does not offer is a document a person can read
 * and not write. server/test/richtext.test.ts asserts the server's list; the
 * swatches below are the browser's rendering of these names and nothing else
 * chooses a colour.
 */
export const PALETTE = [
  'grey', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink',
] as const;

export const EMPTY_DOC: RTDoc = { type: 'doc', schemaVersion: 1, content: [] };

export function isRichTextDoc(v: unknown): v is RTDoc {
  return !!v && typeof v === 'object' && (v as RTDoc).type === 'doc' && Array.isArray((v as RTDoc).content);
}

// ---------------------------------------------------------------------------
// flatten / unflatten
// ---------------------------------------------------------------------------

type BlockKind = 'paragraph' | 'heading' | 'bullet' | 'ordered' | 'task';

interface EBlock {
  id: string;
  kind: BlockKind;
  /** 1..6 for a heading, ignored otherwise. */
  level: number;
  /** 0..MAX_DEPTH. Lists only; a paragraph or heading is always 0. */
  depth: number;
  checked: boolean;
  /** The first number of an ordered run. Read from the first item only. */
  start: number;
  content: RTNode[];
}

/**
 * HOW DEEP A LIST MAY GO IN THE EDITOR. The document schema does not cap it —
 * a list item may hold a list, forever — but a person indenting past three
 * levels in a cleaning checklist has lost the thread, and every level costs two
 * characters of the shadow's width. A document that arrives deeper is rendered
 * at its real depth; Tab will not take it further.
 */
const MAX_DEPTH = 3;

let seq = 0;
const nextId = (): string => `b${++seq}`;

const LIST_KINDS: Record<string, BlockKind> = {
  bulletList: 'bullet', orderedList: 'ordered', taskList: 'task',
};

export function flatten(doc: RTDoc | null | undefined): EBlock[] {
  const out: EBlock[] = [];
  const walk = (nodes: RTNode[], depth: number): void => {
    for (const n of nodes) {
      if (n.type === 'paragraph') {
        out.push(blank('paragraph', { depth: 0, content: n.content ?? [] }));
        continue;
      }
      if (n.type === 'heading') {
        out.push(blank('heading', {
          depth: 0, level: Number(n.attrs?.['level'] ?? 1), content: n.content ?? [],
        }));
        continue;
      }
      const kind = LIST_KINDS[n.type];
      if (!kind) continue;
      const start = kind === 'ordered' ? Number(n.attrs?.['start'] ?? 1) : 1;
      (n.content ?? []).forEach((item, i) => {
        const [para, nested] = item.content ?? [];
        out.push(blank(kind, {
          depth,
          checked: item.attrs?.['checked'] === true,
          start: i === 0 ? start : start + i,
          content: para?.content ?? [],
        }));
        if (nested) walk([nested], depth + 1);
      });
    }
  };
  walk(doc?.content ?? [], 0);
  if (out.length === 0) out.push(blank('paragraph', {}));
  return out;
}

function blank(kind: BlockKind, p: Partial<EBlock>): EBlock {
  return {
    id: nextId(), kind, level: p.level ?? 1, depth: p.depth ?? 0,
    checked: p.checked ?? false, start: p.start ?? 1, content: p.content ?? [],
  };
}

/**
 * REBUILD THE TREE. A run of list lines at the same depth becomes one list; a
 * deeper line becomes the nested list of the item above it.
 *
 * THE SHAPE IT PRODUCES IS THE ONE THE SERVER VALIDATES: a list item is exactly
 * one paragraph and at most one nested list. A deeper line with no item above it
 * (which a person can make by indenting the first line of a list) is pulled back
 * to the shallowest legal depth rather than refused — an editor that silently
 * loses a line is worse than one that straightens it.
 */
export function unflatten(blocks: EBlock[]): RTDoc {
  const content: RTNode[] = [];
  let i = 0;

  const takeList = (depth: number): RTNode | null => {
    const first = blocks[i];
    if (!first || first.depth !== depth || first.kind === 'paragraph' || first.kind === 'heading') return null;
    const kind = first.kind;
    const nodeType = kind === 'bullet' ? 'bulletList' : kind === 'ordered' ? 'orderedList' : 'taskList';
    const items: RTNode[] = [];
    const start = Math.max(1, Math.trunc(first.start || 1));
    while (i < blocks.length) {
      const b = blocks[i]!;
      if (b.depth !== depth || b.kind !== kind) break;
      i += 1;
      const inner: RTNode[] = [{ type: 'paragraph', content: b.content }];
      const nested = blocks[i] && blocks[i]!.depth > depth ? takeList(depth + 1) : null;
      if (nested) inner.push(nested);
      items.push(kind === 'task'
        ? { type: 'taskItem', attrs: { checked: b.checked }, content: inner }
        : { type: 'listItem', content: inner });
    }
    const list: RTNode = { type: nodeType, content: items };
    if (kind === 'ordered') list.attrs = { start };
    return list;
  };

  // A first line that is indented has nothing to hang off. Straighten it once,
  // here, rather than letting takeList() return null forever and drop the line.
  const norm = blocks.map((b) => ({ ...b }));
  let allowed = 0;
  for (const b of norm) {
    if (b.kind === 'paragraph' || b.kind === 'heading') { b.depth = 0; allowed = 0; continue; }
    b.depth = Math.min(b.depth, allowed);
    allowed = Math.min(b.depth + 1, MAX_DEPTH);
  }
  blocks = norm;

  while (i < blocks.length) {
    const b = blocks[i]!;
    if (b.kind === 'paragraph') {
      i += 1;
      // AN EMPTY PARAGRAPH IS DROPPED AT THE TOP LEVEL, which is what the server
      // does too (a blank line is the shadow's block separator, so an empty
      // paragraph has no spelling). Doing it here means the document the editor
      // sends is the document that comes back, so nothing appears to change on
      // its own after a save.
      if (b.content.length) content.push({ type: 'paragraph', content: b.content });
      continue;
    }
    if (b.kind === 'heading') {
      i += 1;
      content.push({
        type: 'heading',
        attrs: { level: Math.min(6, Math.max(1, Math.trunc(b.level || 1))) },
        content: b.content,
      });
      continue;
    }
    const list = takeList(b.depth);
    if (list) content.push(list);
    else i += 1;                                   // cannot happen; never loop
  }
  return { type: 'doc', schemaVersion: 1, content };
}

// ---------------------------------------------------------------------------
// document <-> DOM
// ---------------------------------------------------------------------------

const escHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Innermost first, so the wrapping loop reads outwards. */
const MARK_NESTING = ['strike', 'underline', 'italic', 'bold', 'textColor', 'highlight', 'link'];

function inlineToHtml(nodes: RTNode[]): string {
  let out = '';
  for (const n of nodes) {
    if (n.type === 'hardBreak') { out += '<br>'; continue; }
    if (n.type === 'attachment') {
      const id = String(n.attrs?.['id'] ?? '');
      const title = String(n.attrs?.['title'] ?? '');
      // contenteditable=false makes it ONE THING to the caret: arrow keys step
      // over it and Backspace removes it whole, instead of a person editing the
      // inside of a chip into something that is no longer an attachment.
      out += `<span class="rt-att" contenteditable="false" data-att="${escHtml(id)}"`
           + ` data-title="${escHtml(title)}">${escHtml(title || 'attachment')}</span>`;
      continue;
    }
    let h = escHtml(n.text ?? '');
    const by = new Map((n.marks ?? []).map((m) => [m.type, m]));
    for (const type of MARK_NESTING) {
      const m = by.get(type);
      if (!m) continue;
      if (type === 'bold') h = `<strong>${h}</strong>`;
      else if (type === 'italic') h = `<em>${h}</em>`;
      else if (type === 'underline') h = `<u>${h}</u>`;
      else if (type === 'strike') h = `<s>${h}</s>`;
      else if (type === 'highlight') h = `<span data-hl="${escHtml(String(m.attrs?.['color'] ?? 'yellow'))}">${h}</span>`;
      else if (type === 'textColor') h = `<span data-color="${escHtml(String(m.attrs?.['color'] ?? 'grey'))}">${h}</span>`;
      else if (type === 'link') {
        const href = escHtml(String(m.attrs?.['href'] ?? ''));
        // rel=noopener on every link this console renders — C2b-8. target=_blank
        // without it hands the opened page a window.opener it can navigate.
        h = `<a href="${href}" rel="noopener noreferrer" target="_blank">${h}</a>`;
      }
    }
    out += h || '';
  }
  return out;
}

/**
 * THE SANITISER, AND IT RUNS ON THE WAY IN.
 *
 * Eleven tags are understood. Everything else contributes its TEXT and nothing
 * else — a <div> pasted from a web page, a <script>, a <table>, a Word
 * <span style="mso-...">: the words survive, the markup does not. There is no
 * allow-list of attributes to get wrong because no attribute is read except
 * href, data-hl, data-color, data-att and data-title, each into a typed field.
 */
function domToInline(root: Node): RTNode[] {
  const out: RTNode[] = [];
  const push = (text: string, marks: RTMark[]): void => {
    if (!text) return;
    const last = out[out.length - 1];
    const key = (ms: RTMark[]): string => ms.map((m) => `${m.type}:${JSON.stringify(m.attrs ?? {})}`).sort().join('|');
    if (last && last.type === 'text' && key(last.marks ?? []) === key(marks)) {
      last.text = (last.text ?? '') + text;
      return;
    }
    out.push(marks.length ? { type: 'text', text, marks: [...marks] } : { type: 'text', text });
  };

  const walk = (node: Node, marks: RTMark[]): void => {
    if (node.nodeType === Node.TEXT_NODE) { push(node.nodeValue ?? '', marks); return; }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'br') { out.push({ type: 'hardBreak' }); return; }

    if (el.dataset['att']) {
      const id = String(el.dataset['att']);
      const title = String(el.dataset['title'] ?? '');
      const attrs: Record<string, unknown> = { id };
      if (title) attrs['title'] = title;
      out.push({ type: 'attachment', attrs });
      return;
    }

    let next = marks;
    if (tag === 'strong' || tag === 'b') next = add(next, { type: 'bold' });
    else if (tag === 'em' || tag === 'i') next = add(next, { type: 'italic' });
    else if (tag === 'u') next = add(next, { type: 'underline' });
    else if (tag === 's' || tag === 'strike' || tag === 'del') next = add(next, { type: 'strike' });
    else if (tag === 'a') {
      const href = el.getAttribute('href') ?? '';
      if (href) next = add(next, { type: 'link', attrs: { href } });
    } else if (tag === 'span') {
      const hl = el.dataset['hl'];
      const col = el.dataset['color'];
      if (hl && (PALETTE as readonly string[]).includes(hl)) next = add(next, { type: 'highlight', attrs: { color: hl } });
      if (col && (PALETTE as readonly string[]).includes(col)) next = add(next, { type: 'textColor', attrs: { color: col } });
    }
    for (const c of Array.from(el.childNodes)) walk(c, next);
  };

  for (const c of Array.from(root.childNodes)) walk(c, []);
  return out.filter((n) => n.type !== 'text' || (n.text ?? '') !== '');
}

function add(marks: RTMark[], m: RTMark): RTMark[] {
  return marks.some((x) => x.type === m.type) ? marks : [...marks, m];
}

/** Plain words, for a grid cell that has the document rather than the shadow. */
export function docText(doc: RTDoc | null | undefined, max = 200): string {
  const parts: string[] = [];
  const walk = (n: RTNode): void => {
    if (n.type === 'text') { parts.push(n.text ?? ''); return; }
    if (n.type === 'attachment') { parts.push(String(n.attrs?.['title'] ?? 'attachment')); return; }
    if (n.type === 'hardBreak') { parts.push(' '); return; }
    for (const c of n.content ?? []) walk(c);
    if (n.type === 'paragraph' || n.type === 'heading' || n.type === 'listItem' || n.type === 'taskItem') {
      parts.push(' · ');
    }
  };
  for (const b of doc?.content ?? []) walk(b);
  const s = parts.join('').replace(/( · )+/g, ' · ').replace(/^ · |( · )$/g, '').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// ---------------------------------------------------------------------------
// the reader
// ---------------------------------------------------------------------------

/**
 * READ-ONLY RENDERING, from the document, sanitised — C2b-8.
 *
 * The HTML handed to dangerouslySetInnerHTML is BUILT BY inlineToHtml() from the
 * validated model, never taken from a document field: every piece of text goes
 * through escHtml(), every href was checked by the server's allow-list before it
 * was stored, and every link gets rel="noopener noreferrer". There is no path by
 * which a stored string reaches the DOM as markup.
 */
export function RichTextView({ doc, className }: { doc: RTDoc | null; className?: string }) {
  const blocks = useMemo(() => flatten(doc), [doc]);
  return (
    <div className={`rt-view${className ? ` ${className}` : ''}`}>
      {blocks.map((b) => (
        <BlockShell key={b.id} b={b} editable={false}>
          <span dangerouslySetInnerHTML={{ __html: inlineToHtml(b.content) || '<br>' }} />
        </BlockShell>
      ))}
    </div>
  );
}

function BlockShell(
  { b, editable, children, onToggle }:
  { b: EBlock; editable: boolean; children: React.ReactNode; onToggle?: () => void },
) {
  const marker =
    b.kind === 'bullet' ? <span className="rt-marker" aria-hidden="true">•</span>
      : b.kind === 'ordered' ? <span className="rt-marker">{b.start}.</span>
        : b.kind === 'task' ? (
          <input
            className="rt-check" type="checkbox" checked={b.checked} disabled={!editable}
            onChange={() => onToggle?.()}
            aria-label={b.checked ? 'done' : 'not done'}
          />
        ) : null;
  const cls = `rt-block rt-${b.kind}${b.kind === 'heading' ? ` rt-h${b.level}` : ''}`;
  return (
    <div className={cls} style={b.depth ? { marginLeft: b.depth * 20 } : undefined}>
      {marker}
      <div className="rt-line">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// the editor
// ---------------------------------------------------------------------------

export interface EditorAttachment { id: string; title: string }

/**
 * THE EDITOR. One contenteditable per line, and the caret is the browser's.
 *
 * THE BLOCKS ARE NOT RE-RENDERED WHILE A PERSON TYPES, and that is the whole
 * reason this is shaped the way it is. A controlled contenteditable — one whose
 * innerHTML React rewrites on every keystroke — puts the caret back at the start
 * of the line, every time, and no amount of caret-restoring code makes that
 * pleasant. So each line's HTML is written ONCE when the line's identity
 * changes, the DOM owns it while it is being typed into, and the model is read
 * back from the DOM at the moments that matter: a toolbar command, a structural
 * key, a blur, and Save.
 */
export function RichTextEditor({
  value, editable, attachments, onDirty, register,
}: {
  value: RTDoc | null;
  editable: boolean;
  attachments: EditorAttachment[];
  onDirty?: () => void;
  /** Hands the parent a function that returns the current document. */
  register: (read: () => RTDoc) => void;
}) {
  const [blocks, setBlocks] = useState<EBlock[]>(() => flatten(value));
  const refs = useRef(new Map<string, HTMLDivElement>());
  const [focused, setFocused] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const savedRange = useRef<Range | null>(null);

  useEffect(() => { setBlocks(flatten(value)); }, [value]);

  /** Read every line back out of the DOM into the model. */
  const readBack = useCallback((): EBlock[] => {
    return blocks.map((b) => {
      const el = refs.current.get(b.id);
      return el ? { ...b, content: domToInline(el) } : b;
    });
  }, [blocks]);

  useEffect(() => {
    register(() => unflatten(readBack()));
  }, [register, readBack]);

  const commit = (next: EBlock[]): void => { setBlocks(next); onDirty?.(); };

  const indexOf = (id: string | null): number => blocks.findIndex((b) => b.id === id);

  // -- selection -------------------------------------------------------------
  const remember = (): void => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRange.current = sel.getRangeAt(0).cloneRange();
  };
  const restore = (): void => {
    const r = savedRange.current;
    if (!r) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  };

  /**
   * INLINE FORMATTING. execCommand is deprecated and is still the only thing in
   * a browser that applies a mark to a SELECTION without re-implementing text
   * ranges. `styleWithCSS=false` is what makes it emit <b>, <i>, <u> and <strike>
   * rather than <span style>, which is what domToInline() reads — and it has to
   * be set every time, because it is a document-wide flag another editor on the
   * page could have changed.
   */
  const exec = (cmd: string): void => {
    restore();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(cmd);
    remember();
    onDirty?.();
  };

  /** Wrap the selection in our own span. Highlight and colour have no command. */
  const wrapSpan = (attr: 'hl' | 'color', colour: string): void => {
    restore();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement('span');
    span.dataset[attr === 'hl' ? 'hl' : 'color'] = colour;
    try {
      span.appendChild(range.extractContents());
      range.insertNode(span);
      sel.removeAllRanges();
      const r = document.createRange();
      r.selectNodeContents(span);
      sel.addRange(r);
      savedRange.current = r.cloneRange();
    } catch {
      /* a selection spanning two blocks cannot be wrapped; leave it alone */
    }
    onDirty?.();
  };

  const clearMarks = (): void => {
    restore();
    document.execCommand('removeFormat');
    // removeFormat leaves our own spans and links behind, so they go by hand.
    const sel = window.getSelection();
    const el = sel && sel.anchorNode ? (sel.anchorNode.parentElement?.closest('.rt-line') ?? null) : null;
    if (el) {
      for (const s of Array.from(el.querySelectorAll('span[data-hl], span[data-color], a'))) {
        s.replaceWith(...Array.from(s.childNodes));
      }
    }
    onDirty?.();
  };

  const insertAtCaret = (node: Node): void => {
    restore();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    savedRange.current = range.cloneRange();
    onDirty?.();
  };

  const applyLink = (href: string): void => {
    setLinkOpen(false);
    const clean = href.trim();
    if (!clean) return;
    restore();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const a = document.createElement('a');
    a.setAttribute('href', clean);
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute('target', '_blank');
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      a.textContent = clean;
      insertAtCaret(a);
      return;
    }
    try {
      a.appendChild(range.extractContents());
      range.insertNode(a);
      onDirty?.();
    } catch { /* selection spans blocks */ }
  };

  const insertAttachment = (a: EditorAttachment): void => {
    setAttOpen(false);
    const span = document.createElement('span');
    span.className = 'rt-att';
    span.setAttribute('contenteditable', 'false');
    span.dataset['att'] = a.id;
    span.dataset['title'] = a.title;
    span.textContent = a.title || 'attachment';
    insertAtCaret(span);
  };

  // -- block-level commands --------------------------------------------------
  const setKind = (kind: BlockKind, level = 1): void => {
    const i = indexOf(focused);
    if (i < 0) return;
    const next = readBack();
    const b = next[i]!;
    const same = b.kind === kind && (kind !== 'heading' || b.level === level);
    next[i] = { ...b, kind: same ? 'paragraph' : kind, level, depth: kind === 'heading' || same ? 0 : b.depth };
    commit(next);
  };

  const nudge = (by: number): void => {
    const i = indexOf(focused);
    if (i < 0) return;
    const next = readBack();
    const b = next[i]!;
    if (b.kind === 'paragraph' || b.kind === 'heading') return;
    next[i] = { ...b, depth: Math.max(0, Math.min(MAX_DEPTH, b.depth + by)) };
    commit(next);
  };

  const toggleCheck = (id: string): void => {
    const next = readBack();
    const i = next.findIndex((b) => b.id === id);
    if (i < 0) return;
    next[i] = { ...next[i]!, checked: !next[i]!.checked };
    commit(next);
  };

  // -- keys ------------------------------------------------------------------
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, id: string): void => {
    if (!editable) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const i = indexOf(id);
      const next = readBack();
      const b = next[i]!;
      // SPLIT AT THE CARET rather than always appending, because a person
      // pressing Enter in the middle of a sentence means "break it here". The
      // tail is taken as a Range from the caret to the end of the line.
      const el = refs.current.get(id);
      let tail: RTNode[] = [];
      const sel = window.getSelection();
      if (el && sel && sel.rangeCount) {
        const r = sel.getRangeAt(0).cloneRange();
        const end = document.createRange();
        end.selectNodeContents(el);
        end.setStart(r.endContainer, r.endOffset);
        const frag = end.cloneContents();
        const holder = document.createElement('div');
        holder.appendChild(frag);
        tail = domToInline(holder);
        end.deleteContents();
        next[i] = { ...b, content: domToInline(el) };
      }
      // A heading's Enter starts a paragraph — nobody wants two headings in a
      // row by accident. A list item's Enter starts another item at the same
      // depth, which is what every list in every editor does.
      const kind: BlockKind = b.kind === 'heading' ? 'paragraph' : b.kind;
      const made = blank(kind, { depth: b.kind === 'heading' ? 0 : b.depth, content: tail, start: b.start + 1 });
      next.splice(i + 1, 0, made);
      commit(next);
      queueMicrotask(() => refs.current.get(made.id)?.focus());
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      nudge(e.shiftKey ? -1 : 1);
      queueMicrotask(() => refs.current.get(id)?.focus());
      return;
    }
    if (e.key === 'Backspace') {
      const el = refs.current.get(id);
      const sel = window.getSelection();
      const atStart = !!sel && sel.isCollapsed && !!el
        && sel.anchorOffset === 0
        && (sel.anchorNode === el || el.firstChild === sel.anchorNode || el.childNodes.length === 0);
      if (!atStart) return;
      const i = indexOf(id);
      if (i <= 0) return;
      e.preventDefault();
      const next = readBack();
      const b = next[i]!;
      // A LIST LINE STRAIGHTENS BEFORE IT MERGES: Backspace at the start of an
      // indented bullet outdents it, then makes it a paragraph, and only then
      // joins it to the line above. Three presses to undo three decisions.
      if (b.depth > 0) { next[i] = { ...b, depth: b.depth - 1 }; commit(next); queueMicrotask(() => el?.focus()); return; }
      if (b.kind !== 'paragraph') { next[i] = { ...b, kind: 'paragraph' }; commit(next); queueMicrotask(() => el?.focus()); return; }
      const prev = next[i - 1]!;
      next[i - 1] = { ...prev, content: [...prev.content, ...b.content] };
      next.splice(i, 1);
      commit(next);
      queueMicrotask(() => refs.current.get(prev.id)?.focus());
    }
  };

  /**
   * A PASTE IS TEXT AND OUR OWN CHIPS, NEVER THE CLIPBOARD'S HTML.
   *
   * The default paste inserts whatever markup the source had — Word's spans,
   * a site's divs, a tracking pixel — into a contenteditable. domToInline()
   * would drop all of it on the way back out, so nothing unsafe could be STORED
   * either way; what this prevents is the twenty seconds in between, where the
   * screen shows a person a formatting they have not got and cannot keep.
   */
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (!editable) return;
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    const lines = text.split(/\r\n?|\n/);
    document.execCommand('insertText', false, lines[0] ?? '');
    if (lines.length > 1) {
      // Multi-line paste becomes multi-line document: one paragraph per line,
      // after the current one, in order.
      const i = indexOf(focused);
      const next = readBack();
      const made = lines.slice(1).map((l) => blank('paragraph', { content: l ? [{ type: 'text', text: l }] : [] }));
      next.splice(i + 1, 0, ...made);
      commit(next);
    }
  };

  // -- render ----------------------------------------------------------------
  const current = blocks.find((b) => b.id === focused) ?? null;

  return (
    <div className="rt-editor">
      {editable && (
        <Toolbar
          current={current}
          attachments={attachments}
          onExec={exec}
          onKind={setKind}
          onWrap={wrapSpan}
          onClear={clearMarks}
          onLink={() => { remember(); setLinkOpen(true); }}
          onAttach={() => { remember(); setAttOpen(true); }}
          onIndent={nudge}
        />
      )}
      {linkOpen && <LinkPrompt onCancel={() => setLinkOpen(false)} onApply={applyLink} />}
      {attOpen && (
        <AttachPick
          attachments={attachments}
          onCancel={() => setAttOpen(false)}
          onPick={insertAttachment}
        />
      )}
      <div className="rt-doc">
        {blocks.map((b) => (
          <BlockShell key={b.id} b={b} editable={editable} onToggle={() => toggleCheck(b.id)}>
            <Line
              b={b}
              editable={editable}
              refs={refs}
              onFocus={() => setFocused(b.id)}
              onKeyDown={(e) => onKeyDown(e, b.id)}
              onPaste={onPaste}
              onSelect={remember}
              onInput={() => onDirty?.()}
            />
          </BlockShell>
        ))}
      </div>
    </div>
  );
}

/**
 * ONE LINE. Its HTML is written by an effect keyed on the block's IDENTITY, not
 * on its content — see the editor's own comment. While a person is typing, this
 * element's children belong to the browser.
 */
function Line({
  b, editable, refs, onFocus, onKeyDown, onPaste, onSelect, onInput,
}: {
  b: EBlock;
  editable: boolean;
  refs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  onFocus: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onSelect: () => void;
  onInput: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = inlineToHtml(b.content);
    refs.current.set(b.id, el);
    return () => { refs.current.delete(b.id); };
    // b.content is DELIBERATELY not a dependency: re-writing innerHTML on every
    // keystroke is what puts the caret back at the start of the line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id]);

  return (
    <div
      ref={ref}
      className="rt-line-edit"
      contentEditable={editable}
      suppressContentEditableWarning
      spellCheck
      role={editable ? 'textbox' : undefined}
      aria-multiline={editable ? 'false' : undefined}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onMouseUp={onSelect}
      onKeyUp={onSelect}
      onInput={onInput}
    />
  );
}

function Toolbar({
  current, attachments, onExec, onKind, onWrap, onClear, onLink, onAttach, onIndent,
}: {
  current: EBlock | null;
  attachments: EditorAttachment[];
  onExec: (cmd: string) => void;
  onKind: (kind: BlockKind, level?: number) => void;
  onWrap: (attr: 'hl' | 'color', colour: string) => void;
  onClear: () => void;
  onLink: () => void;
  onAttach: () => void;
  onIndent: (by: number) => void;
}) {
  // EVERY BUTTON PREVENTS DEFAULT ON MOUSEDOWN. Without it the browser moves
  // focus to the button, the selection in the line is lost, and the command
  // applies to nothing — which looks exactly like a broken button.
  const hold = (fn: () => void) => ({
    onMouseDown: (e: React.MouseEvent) => { e.preventDefault(); fn(); },
  });
  const on = (kind: BlockKind, level?: number): string =>
    current && current.kind === kind && (kind !== 'heading' || current.level === level) ? ' on' : '';

  return (
    <div className="rt-toolbar" role="toolbar" aria-label="Formatting">
      <div className="rt-group">
        {[1, 2, 3].map((l) => (
          <button key={l} type="button" className={`rt-btn${on('heading', l)}`}
                  title={`Heading ${l}`} {...hold(() => onKind('heading', l))}>H{l}</button>
        ))}
      </div>
      <div className="rt-group">
        <button type="button" className="rt-btn" title="Bold" {...hold(() => onExec('bold'))}><b>B</b></button>
        <button type="button" className="rt-btn" title="Italic" {...hold(() => onExec('italic'))}><i>I</i></button>
        <button type="button" className="rt-btn" title="Underline" {...hold(() => onExec('underline'))}><u>U</u></button>
        <button type="button" className="rt-btn" title="Strikethrough" {...hold(() => onExec('strikeThrough'))}><s>S</s></button>
      </div>
      <div className="rt-group">
        <Swatches label="Highlight" glyph="▭" onPick={(c) => onWrap('hl', c)} />
        <Swatches label="Text colour" glyph="A" onPick={(c) => onWrap('color', c)} />
        <button type="button" className="rt-btn" title="Clear formatting" {...hold(onClear)}>✕</button>
      </div>
      <div className="rt-group">
        <button type="button" className={`rt-btn${on('bullet')}`} title="Bullet list" {...hold(() => onKind('bullet'))}>•</button>
        <button type="button" className={`rt-btn${on('ordered')}`} title="Numbered list" {...hold(() => onKind('ordered'))}>1.</button>
        <button type="button" className={`rt-btn${on('task')}`} title="Check list" {...hold(() => onKind('task'))}>☑</button>
        <button type="button" className="rt-btn" title="Outdent (Shift+Tab)" {...hold(() => onIndent(-1))}>⇤</button>
        <button type="button" className="rt-btn" title="Indent (Tab)" {...hold(() => onIndent(1))}>⇥</button>
      </div>
      <div className="rt-group">
        <button type="button" className="rt-btn" title="Link" {...hold(onLink)}>🔗</button>
        <button type="button" className="rt-btn" title="Insert an attachment of this record"
                disabled={attachments.length === 0} {...hold(onAttach)}>📎</button>
      </div>
    </div>
  );
}

function Swatches({ label, glyph, onPick }: { label: string; glyph: string; onPick: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="rt-swatchwrap">
      <button
        type="button" className="rt-btn" title={label}
        onMouseDown={(e) => { e.preventDefault(); setOpen((v) => !v); }}
      >{glyph}</button>
      {open && (
        <span className="rt-swatches" role="menu" aria-label={label}>
          {PALETTE.map((c) => (
            <button
              key={c} type="button" className={`rt-swatch rt-c-${c}`} title={c} aria-label={c}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); onPick(c); }}
            />
          ))}
        </span>
      )}
    </span>
  );
}

function LinkPrompt({ onApply, onCancel }: { onApply: (href: string) => void; onCancel: () => void }) {
  const [href, setHref] = useState('https://');
  return (
    <div className="rt-prompt">
      <label htmlFor="rt-href">Link to</label>
      <input
        id="rt-href" autoFocus value={href}
        onChange={(e) => setHref(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onApply(href); if (e.key === 'Escape') onCancel(); }}
      />
      <button className="action" onMouseDown={(e) => { e.preventDefault(); onCancel(); }}>Cancel</button>
      <button className="action on" onMouseDown={(e) => { e.preventDefault(); onApply(href); }}>Link</button>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
        http, https, mailto and tel only. A javascript: or data: link is refused when it is saved,
        not quietly stripped when it is shown.
      </p>
    </div>
  );
}

function AttachPick({
  attachments, onPick, onCancel,
}: { attachments: EditorAttachment[]; onPick: (a: EditorAttachment) => void; onCancel: () => void }) {
  return (
    <div className="rt-prompt">
      <span className="muted">This record’s attachments:</span>
      {attachments.map((a) => (
        <button key={a.id} className="action"
                onMouseDown={(e) => { e.preventDefault(); onPick(a); }}>{a.title || 'attachment'}</button>
      ))}
      <button className="action" onMouseDown={(e) => { e.preventDefault(); onCancel(); }}>Cancel</button>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
        A document may only show attachments of its own record — that is what makes an attachment
        readable exactly when its record is. Add one to the row first and it appears here.
      </p>
    </div>
  );
}
